import React, { Fragment, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom'
import { Fade } from "react-awesome-reveal";
import { Link } from 'react-router-dom'
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import Scrollbar from '../../components/scrollbar/scrollbar';
import CtaSection from '../../components/CtaSection/CtaSection';
import Industries from '../../api/industries';
import Bg from '../../images/bg/page_bg01.jpg'
import icon from '../../images/icon/ser-01.svg'

// Helper function to map highlight content to appropriate icons
const getHighlightIcon = (highlightText) => {
    const text = highlightText.toLowerCase();
    
    // SPECIFIC HIGHLIGHT MAPPINGS (Most specific patterns first)
    // Real Estate - IDX/MLS Integration
    if ((text.includes('idx') || text.includes('mls')) && text.includes('integration')) return 'fas fa-project-diagram';
    
    // Insurance - Mobile-First Customer Portal
    if (text.includes('mobile-first') && text.includes('customer')) return 'fas fa-mobile-alt';
    
    // Crypto - NFT with IPFS
    if (text.includes('nft') && (text.includes('ipfs') || text.includes('royalty'))) return 'fas fa-certificate';
    
    // Private Equity - Deal Flow with CRM
    if (text.includes('deal flow') && text.includes('crm')) return 'fas fa-stream';
    
    // Private Equity - LP Investor Portal
    if ((text.includes('lp investor') || text.includes('lp portal')) || (text.includes('capital calls') && text.includes('distributions'))) return 'fas fa-columns';
    
    // Private Equity - Portfolio Company Performance Dashboards
    if (text.includes('portfolio company') && text.includes('performance') && text.includes('dashboard')) return 'fas fa-tachometer-alt';
    
    // Education - Student Information Systems with Parent/Teacher Portals
    if (text.includes('student information') && (text.includes('parent') || text.includes('teacher'))) return 'fas fa-window-restore';
    
    // Education - AI-Powered Adaptive Learning
    if ((text.includes('ai-powered') || text.includes('ai powered')) && (text.includes('adaptive learning') || text.includes('personalized content'))) return 'fas fa-robot';
    
    // Automotive - EV Charging Network Management
    if ((text.includes('ev charging network') || text.includes('charging network management')) && text.includes('payment')) return 'fas fa-charging-station';
    
    // Automotive - Connected Car with OTA Updates
    if (text.includes('connected car') && (text.includes('ota') || text.includes('telematics'))) return 'fas fa-broadcast-tower';
    
    // Automotive - Vehicle Marketplace with VIN Verification
    if (text.includes('vehicle marketplace') && text.includes('vin')) return 'fas fa-search-location';
    
    // GENERIC PATTERNS (Keep existing patterns below specific ones)
    // Architecture & Infrastructure
    if (text.includes('multi-tenant') || text.includes('architecture')) return 'fas fa-sitemap';
    if (text.includes('cloud') || text.includes('infrastructure')) return 'fas fa-cloud';
    if (text.includes('microservices')) return 'fas fa-cubes';
    if (text.includes('scalable') || text.includes('scalability')) return 'fas fa-expand-arrows-alt';
    
    // Payment & Billing
    if (text.includes('subscription') || text.includes('billing')) return 'fas fa-file-invoice-dollar';
    if (text.includes('payment')) return 'fas fa-credit-card';
    
    // API & Integration
    if (text.includes('api')) return 'fas fa-code';
    if (text.includes('integration')) return 'fas fa-plug';
    if (text.includes('webhook')) return 'fas fa-exchange-alt';
    
    // Analytics & Reporting
    if (text.includes('analytics') || text.includes('dashboard')) return 'fas fa-chart-line';
    if (text.includes('reporting') || text.includes('metrics')) return 'fas fa-chart-bar';
    if (text.includes('insight')) return 'fas fa-lightbulb';
    
    // Security & Compliance
    if (text.includes('security') || text.includes('secure')) return 'fas fa-shield-alt';
    if (text.includes('compliance') || text.includes('audit')) return 'fas fa-clipboard-check';
    if (text.includes('encryption')) return 'fas fa-lock';
    if (text.includes('authentication') || text.includes('auth')) return 'fas fa-user-shield';
    
    // Legal & Documents
    if (text.includes('case management') || text.includes('matter')) return 'fas fa-briefcase';
    if (text.includes('document') || text.includes('contract')) return 'fas fa-file-alt';
    if (text.includes('e-signature') || text.includes('signature')) return 'fas fa-signature';
    if (text.includes('research')) return 'fas fa-search';
    
    // Real Estate
    if (text.includes('property management')) return 'fas fa-building';
    if (text.includes('virtual tour') || text.includes('3d') || text.includes('ar')) return 'fas fa-vr-cardboard';
    if (text.includes('marketplace')) return 'fas fa-store';
    if (text.includes('crm')) return 'fas fa-users-cog';
    
    // Insurance
    if (text.includes('claims')) return 'fas fa-file-medical';
    if (text.includes('policy') || text.includes('underwriting')) return 'fas fa-file-contract';
    if (text.includes('risk assessment') || text.includes('risk')) return 'fas fa-chart-line';
    if (text.includes('customer portal') || text.includes('self-service')) return 'fas fa-user-circle';
    
    // Blockchain & Crypto
    if (text.includes('smart contract')) return 'fas fa-file-code';
    if (text.includes('defi') || text.includes('decentralized')) return 'fas fa-coins';
    if (text.includes('nft')) return 'fas fa-images';
    if (text.includes('blockchain')) return 'fab fa-ethereum';
    if (text.includes('wallet')) return 'fas fa-wallet';
    
    // Private Equity & Finance
    if (text.includes('portfolio')) return 'fas fa-briefcase';
    if (text.includes('deal flow') || text.includes('investment')) return 'fas fa-handshake';
    if (text.includes('valuation') || text.includes('modeling')) return 'fas fa-calculator';
    if (text.includes('investor')) return 'fas fa-user-tie';
    if (text.includes('trading') || text.includes('forex')) return 'fas fa-chart-line';
    if (text.includes('banking')) return 'fas fa-university';
    
    // Education
    if (text.includes('learning') || text.includes('lms')) return 'fas fa-graduation-cap';
    if (text.includes('course') || text.includes('curriculum')) return 'fas fa-book-open';
    if (text.includes('virtual classroom') || text.includes('video')) return 'fas fa-video';
    if (text.includes('assessment') || text.includes('grading')) return 'fas fa-tasks';
    if (text.includes('student')) return 'fas fa-user-graduate';
    
    // Healthcare
    if (text.includes('telemedicine') || text.includes('telehealth')) return 'fas fa-laptop-medical';
    if (text.includes('ehr') || text.includes('emr') || text.includes('health record')) return 'fas fa-file-medical-alt';
    if (text.includes('patient')) return 'fas fa-user-md';
    if (text.includes('prescription') || text.includes('medication')) return 'fas fa-prescription';
    if (text.includes('diagnostic') || text.includes('medical ai')) return 'fas fa-brain';
    
    // Automotive
    if (text.includes('fleet')) return 'fas fa-car';
    if (text.includes('connected car') || text.includes('iot') || text.includes('telematics')) return 'fas fa-broadcast-tower';
    if (text.includes('ev') || text.includes('charging') || text.includes('electric')) return 'fas fa-charging-station';
    if (text.includes('gps') || text.includes('tracking')) return 'fas fa-map-marked-alt';
    
    // Communication & Collaboration
    if (text.includes('communication') || text.includes('messaging')) return 'fas fa-comments';
    if (text.includes('collaboration')) return 'fas fa-users';
    if (text.includes('notification')) return 'fas fa-bell';
    
    // Automation & AI
    if (text.includes('automation') || text.includes('automated')) return 'fas fa-robot';
    if (text.includes('ai') || text.includes('artificial intelligence') || text.includes('machine learning')) return 'fas fa-brain';
    if (text.includes('workflow')) return 'fas fa-project-diagram';
    
    // Data & Storage
    if (text.includes('data') || text.includes('database')) return 'fas fa-database';
    if (text.includes('storage') || text.includes('backup')) return 'fas fa-hdd';
    
    // Mobile & Web
    if (text.includes('mobile')) return 'fas fa-mobile-alt';
    if (text.includes('web') || text.includes('responsive')) return 'fas fa-laptop';
    
    // Default
    return 'fas fa-check-circle';
};

// Helper function to intelligently map technology items to FontAwesome icons
const getTechIcon = (techText) => {
    const text = techText.toLowerCase();
    
    // Frontend frameworks
    if (text.includes('react') || text.includes('next.js')) return 'fab fa-react';
    if (text.includes('angular')) return 'fab fa-angular';
    if (text.includes('vue')) return 'fab fa-vuejs';
    
    // Backend & Languages
    if (text.includes('node.js') || text.includes('node')) return 'fab fa-node-js';
    if (text.includes('python') || text.includes('tensorflow') || text.includes('django')) return 'fab fa-python';
    if (text.includes('java') && !text.includes('javascript')) return 'fab fa-java';
    if (text.includes('php')) return 'fab fa-php';
    
    // Databases
    if (text.includes('postgresql') || text.includes('mysql') || text.includes('sql') || text.includes('database')) return 'fas fa-database';
    if (text.includes('mongodb') || text.includes('nosql')) return 'fas fa-database';
    if (text.includes('redis') || text.includes('cache')) return 'fas fa-memory';
    
    // DevOps & Infrastructure
    if (text.includes('docker') || text.includes('container')) return 'fab fa-docker';
    if (text.includes('kubernetes')) return 'fas fa-dharmachakra';
    if (text.includes('aws') || text.includes('amazon web services')) return 'fab fa-aws';
    if (text.includes('azure') || text.includes('microsoft cloud')) return 'fab fa-microsoft';
    if (text.includes('gcp') || text.includes('google cloud')) return 'fab fa-google';
    if (text.includes('git') && !text.includes('digital')) return 'fab fa-git-alt';
    
    // Security & Auth
    if (text.includes('auth0') || text.includes('cognito') || text.includes('authentication') || text.includes('auth')) return 'fas fa-user-shield';
    if (text.includes('encryption') || text.includes('security') || text.includes('ssl') || text.includes('tls')) return 'fas fa-lock';
    if (text.includes('compliance') || text.includes('audit')) return 'fas fa-shield-alt';
    
    // Payment & Commerce
    if (text.includes('stripe') || text.includes('paypal') || text.includes('payment')) return 'fas fa-credit-card';
    if (text.includes('billing') || text.includes('subscription')) return 'fas fa-file-invoice-dollar';
    
    // AI & ML
    if (text.includes('ai') || text.includes('machine learning') || text.includes('ml')) return 'fas fa-brain';
    if (text.includes('nlp') || text.includes('natural language')) return 'fas fa-language';
    if (text.includes('computer vision') || text.includes('ocr')) return 'fas fa-eye';
    
    // Communication
    if (text.includes('twilio') || text.includes('sms')) return 'fas fa-sms';
    if (text.includes('email') || text.includes('sendgrid')) return 'fas fa-envelope';
    if (text.includes('notification') || text.includes('push')) return 'fas fa-bell';
    
    // Blockchain & Crypto
    if (text.includes('blockchain') || text.includes('smart contract')) return 'fab fa-ethereum';
    if (text.includes('bitcoin') || text.includes('crypto')) return 'fab fa-bitcoin';
    if (text.includes('web3') || text.includes('defi')) return 'fas fa-coins';
    
    // Analytics & Monitoring
    if (text.includes('analytics') || text.includes('dashboard') || text.includes('reporting')) return 'fas fa-chart-bar';
    if (text.includes('monitoring') || text.includes('observability')) return 'fas fa-chart-line';
    
    // API & Integration
    if (text.includes('api') || text.includes('graphql') || text.includes('rest')) return 'fas fa-exchange-alt';
    if (text.includes('integration') || text.includes('webhook')) return 'fas fa-plug';
    
    // Mobile
    if (text.includes('ios') || text.includes('swift')) return 'fab fa-apple';
    if (text.includes('android') || text.includes('kotlin')) return 'fab fa-android';
    if (text.includes('react native') || text.includes('mobile app')) return 'fas fa-mobile-alt';
    
    // Maps & Location
    if (text.includes('map') || text.includes('location') || text.includes('gps')) return 'fas fa-map-marked-alt';
    
    // Media & 3D
    if (text.includes('3d') || text.includes('three.js') || text.includes('webgl')) return 'fas fa-cube';
    if (text.includes('video') || text.includes('streaming')) return 'fas fa-video';
    if (text.includes('ar') || text.includes('augmented reality') || text.includes('vr')) return 'fas fa-vr-cardboard';
    
    // IoT
    if (text.includes('iot') || text.includes('smart building') || text.includes('sensor')) return 'fas fa-broadcast-tower';
    
    // Default fallback
    return 'fas fa-cog';
};

const IndustryPage = () => {
    const { slug } = useParams();
    const industry = Industries.find(item => item.slug === slug);

    useEffect(() => {
        window.scrollTo(10, 0);
    }, []);

    const ClickHandler = () => {
        window.scrollTo(10, 0);
    };

    // If industry not found, redirect to home
    if (!industry) {
        return <Navigate to="/" replace />;
    }

    return (
        <Fragment>
            <Header />
            <main className="main">
                {/* Hero Section */}
                <section className="page-title pt-200 pos-rel bg_img" style={{ backgroundImage: `url(${Bg})` }}>
                    <div className="container">
                        <div className="page-title-wrap">
                            <div className="row mt-none-30 align-items-end">
                                <div className="col-lg-9 mt-30">
                                    <div className="page-title-box">
                                        <span className="sub-title"><img src={icon} alt="" /> {industry.tagline}</span>
                                        <h2 className="title">{industry.hero.title}</h2>
                                        <p style={{ fontSize: '18px', marginTop: '20px', opacity: '0.9' }}>
                                            {industry.hero.subtitle}
                                        </p>
                                    </div>
                                    <div className="row mt-40">
                                        {industry.hero.highlights.map((highlight, index) => (
                                            <div className="col-md-6 mb-20" key={index}>
                                                <div className="xb-highlight-item" style={{
                                                    padding: '20px 25px',
                                                    background: '#0b1957',
                                                    borderRadius: '12px',
                                                    border: '2px solid rgba(255, 255, 255, 0.1)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 4px 20px rgba(11, 25, 87, 0.3)',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(11, 25, 87, 0.4)';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(11, 25, 87, 0.3)';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                                }}>
                                                    <div style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        borderRadius: '12px',
                                                        background: 'linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginRight: '18px',
                                                        flexShrink: 0,
                                                        boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)'
                                                    }}>
                                                        <i className={getHighlightIcon(highlight)} style={{ color: '#0b1957', fontSize: '20px' }}></i>
                                                    </div>
                                                    <span style={{ fontSize: '15px', lineHeight: '1.6', color: '#ffffff', fontWeight: '500' }}>
                                                        {highlight}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="xb-btn mt-40">
                                        <Link onClick={ClickHandler} to="/contact" className="thm-btn thm-btn--aso">
                                            {industry.cta.buttonText}
                                        </Link>
                                    </div>
                                </div>
                                <div className="col-lg-3 mt-30">
                                    <div className="xb-item--icon">
                                        <img src={industry.icon} alt={industry.title} style={{ width: '100%', maxWidth: '200px' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Overview Section */}
                <section className="about-section pt-130 pb-100">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6">
                                <Fade direction='left' triggerOnce duration={1000}>
                                    <div className="sec-title--two">
                                        <h2 className="title mb-30">{industry.overview.title}</h2>
                                        <p className="content">{industry.overview.description}</p>
                                    </div>
                                </Fade>
                            </div>
                            <div className="col-lg-6">
                                <Fade direction='right' triggerOnce duration={1000}>
                                    <div className="row">
                                        {industry.overview.stats.map((stat, index) => (
                                            <div className="col-md-4 mb-30" key={index}>
                                                <div className="xb-funfact text-center" style={{
                                                    padding: '35px 20px',
                                                    background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                                                    borderRadius: '15px',
                                                    border: '2px solid #e8ecff',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(11, 25, 87, 0.15)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}>
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        borderRadius: '10px',
                                                        background: '#0b1957',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        margin: '0 auto 20px'
                                                    }}>
                                                        <i className="fas fa-chart-line" style={{ color: '#fff', fontSize: '20px' }}></i>
                                                    </div>
                                                    <h2 className="xb-item--number mb-10" style={{ fontSize: '36px', fontWeight: '700', color: '#0b1957' }}>{stat.value}</h2>
                                                    <p className="xb-item--title" style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>{stat.label}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Fade>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className="service-section sec-bg pt-130 pb-100">
                    <div className="container">
                        <div className="sec-title--two text-center mb-60">
                            <Fade direction='down' triggerOnce duration={1000}>
                                <h2 className="title">Our {industry.title} Solutions</h2>
                                <p className="content mt-20">Comprehensive technology services tailored for your industry</p>
                            </Fade>
                        </div>
                        <div className="row">
                            {industry.services.map((service, index) => (
                                <div className="col-lg-6 mb-30" key={index}>
                                    <Fade direction='up' triggerOnce duration={1000} delay={index * 100}>
                                        <div className="xb-service" style={{
                                            padding: '40px 35px',
                                            background: '#ffffff',
                                            borderRadius: '15px',
                                            border: '2px solid #f3f4f6',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            transition: 'all 0.4s ease',
                                            height: '100%'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-8px)';
                                            e.currentTarget.style.borderColor = '#0b1957';
                                            e.currentTarget.style.boxShadow = '0 15px 40px rgba(11, 25, 87, 0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.borderColor = '#f3f4f6';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: '100px',
                                                height: '100px',
                                                background: 'linear-gradient(135deg, rgba(11, 25, 87, 0.05) 0%, transparent 100%)',
                                                borderRadius: '0 15px 0 100%'
                                            }}></div>
                                            <div style={{
                                                width: '70px',
                                                height: '70px',
                                                borderRadius: '15px',
                                                background: 'linear-gradient(135deg, #0b1957 0%, #1e3a8a 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: '25px',
                                                position: 'relative',
                                                boxShadow: '0 8px 20px rgba(11, 25, 87, 0.2)'
                                            }}>
                                                <i className={service.icon.startsWith('fa') ? service.icon : `fas fa-${service.icon}`} style={{ color: '#fff', fontSize: '28px' }}></i>
                                            </div>
                                            <h3 className="xb-item--title" style={{ fontSize: '22px', fontWeight: '600', marginBottom: '15px', color: '#0b1957' }}>{service.title}</h3>
                                            <p className="xb-item--content" style={{ fontSize: '15px', lineHeight: '1.7', color: '#6b7280', margin: 0 }}>{service.description}</p>
                                        </div>
                                    </Fade>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Technologies Section */}
                <section className="technology-section pt-130 pb-130">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-5">
                                <Fade direction='left' triggerOnce duration={1000}>
                                    <div className="sec-title--two mb-50">
                                        <h2 className="title mb-30">{industry.technologies.title}</h2>
                                        <p>We leverage cutting-edge technologies to deliver robust, scalable solutions.</p>
                                    </div>
                                </Fade>
                            </div>
                            <div className="col-lg-7">
                                <Fade direction='right' triggerOnce duration={1000}>
                                    <div className="row">
                                        {industry.technologies.items.map((tech, index) => (
                                            <div className="col-md-6 mb-20" key={index}>
                                                <div style={{
                                                    padding: '18px 25px',
                                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                                                    borderRadius: '12px',
                                                    border: '2px solid #e8ecff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, #0b1957 0%, #1e3a8a 100%)';
                                                    e.currentTarget.style.borderColor = '#0b1957';
                                                    e.currentTarget.style.transform = 'translateX(5px)';
                                                    const icon = e.currentTarget.querySelector('.tech-icon');
                                                    const text = e.currentTarget.querySelector('.tech-text');
                                                    if (icon) icon.style.color = '#fff';
                                                    if (text) text.style.color = '#fff';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)';
                                                    e.currentTarget.style.borderColor = '#e8ecff';
                                                    e.currentTarget.style.transform = 'translateX(0)';
                                                    const icon = e.currentTarget.querySelector('.tech-icon');
                                                    const text = e.currentTarget.querySelector('.tech-text');
                                                    if (icon) icon.style.color = '#0b1957';
                                                    if (text) text.style.color = '#1f2937';
                                                }}>
                                                    <div style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        borderRadius: '8px',
                                                        background: 'rgba(11, 25, 87, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginRight: '15px',
                                                        flexShrink: 0
                                                    }}>
                                                        <i className={`${getTechIcon(tech)} tech-icon`} style={{ color: '#0b1957', fontSize: '16px', transition: 'color 0.3s ease' }}></i>
                                                    </div>
                                                    <span className="tech-text" style={{ fontSize: '15px', fontWeight: '500', color: '#1f2937', transition: 'color 0.3s ease' }}>
                                                        {tech}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Fade>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Case Study Section */}
                <section className="case-study-section sec-bg pt-130 pb-130">
                    <div className="container">
                        <Fade direction='up' triggerOnce duration={1000}>
                            <div className="xb-case-study">
                                <div className="row align-items-center">
                                    <div className="col-lg-7">
                                        <div className="sec-title--two">
                                            <span className="sub-title"><img src={icon} alt="" /> CLIENT SUCCESS</span>
                                            <h2 className="title mb-25">{industry.caseStudy.title}</h2>
                                            <p className="content">{industry.caseStudy.description}</p>
                                        </div>
                                    </div>
                                    <div className="col-lg-5">
                                        <div className="results mt-30">
                                            {industry.caseStudy.results.map((result, index) => (
                                                <div className="result-item mb-20" key={index} style={{
                                                    padding: '20px 25px',
                                                    background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                                                    borderRadius: '12px',
                                                    border: '2px solid #e8ecff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateX(5px)';
                                                    e.currentTarget.style.boxShadow = '0 5px 20px rgba(11, 25, 87, 0.1)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateX(0)';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}>
                                                    <div style={{
                                                        width: '45px',
                                                        height: '45px',
                                                        borderRadius: '10px',
                                                        background: 'linear-gradient(135deg, #0b1957 0%, #1e3a8a 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginRight: '18px',
                                                        flexShrink: 0,
                                                        boxShadow: '0 4px 12px rgba(11, 25, 87, 0.2)'
                                                    }}>
                                                        <i className="fas fa-chart-line" style={{ color: '#fff', fontSize: '18px' }}></i>
                                                    </div>
                                                    <span style={{ fontSize: '15px', lineHeight: '1.6', fontWeight: '500', color: '#1f2937' }}>{result}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Fade>
                    </div>
                </section>

                {/* CTA Section */}
                <CtaSection 
                    title={industry.cta.title}
                    subtitle={industry.cta.subtitle}
                    buttonText={industry.cta.buttonText}
                    buttonLink="/contact"
                />

            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default IndustryPage;
